import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  ClipboardList,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  Download,
  Trash2,
  FileText,
  Clock,
  ChevronDown,
  Plus,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFormAutosave } from "@/hooks/useAutosave";

const SOP_TEMPLATES = {
  client_onboarding: {
    title: "Client Onboarding Process",
    description: "Step-by-step process for bringing new clients into your workflow",
    icon: "👋"
  },
  project_kickoff: {
    title: "Project Kickoff Checklist",
    description: "Everything needed to start a new project smoothly",
    icon: "🚀"
  },
  feedback_collection: {
    title: "Feedback Collection Process",
    description: "How to gather, organize, and act on client feedback",
    icon: "💬"
  },
  deliverable_handoff: {
    title: "Deliverable Handoff Process",
    description: "Standardized process for delivering work to clients",
    icon: "📦"
  },
  invoice_followup: {
    title: "Invoice & Payment Follow-up",
    description: "Process for managing invoices and following up on payments",
    icon: "💰"
  },
  project_closure: {
    title: "Project Closure Checklist",
    description: "Steps to properly close out a completed project",
    icon: "✅"
  },
  custom: {
    title: "Custom SOP",
    description: "Create a custom standard operating procedure",
    icon: "⚙️"
  }
};

export default function SOPGenerator({ user, usage, refreshUsage }) {
  const navigate = useNavigate();
  const isOutOfCredits =
    user?.tier === "free" &&
    !usage?.is_demo &&
    typeof usage?.llm_credits_remaining === "number" &&
    usage.llm_credits_remaining <= 0;
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Auto-save form state
  const { values: draftValues, setValues: setDraftValues, clearDraft, hasDraft } = useFormAutosave('sop_generator', {
    selectedTemplate: "client_onboarding",
    customTitle: "",
    customDetails: "",
    serviceType: "web_design"
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState(draftValues.selectedTemplate || "client_onboarding");
  const [customTitle, setCustomTitle] = useState(draftValues.customTitle || "");
  const [customDetails, setCustomDetails] = useState(draftValues.customDetails || "");
  const [serviceType, setServiceType] = useState(draftValues.serviceType || "web_design");
  
  // Sync to auto-save
  useEffect(() => {
    setDraftValues({ selectedTemplate, customTitle, customDetails, serviceType });
  }, [selectedTemplate, customTitle, customDetails, serviceType]);
  
  const [currentSop, setCurrentSop] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);

  useEffect(() => {
    fetchSops();
  }, []);

  const fetchSops = async () => {
    setLoading(true);
    try {
      const res = await api.get("/sops");
      setSops(res.data.items || []);
    } catch (e) {
      console.error("Error fetching SOPs:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (isOutOfCredits) {
      navigate("/billing");
      return;
    }
    setGenerating(true);
    try {
      const title = selectedTemplate === "custom" ? customTitle : SOP_TEMPLATES[selectedTemplate].title;

      const res = await api.post("/sop/generate", {
        template_key: selectedTemplate,
        title,
        notes: customDetails,
        industry: serviceType
      });

      setCurrentSop({
        id: res.data.sop_id,
        title: res.data.title,
        body_md: res.data.body_md,
        template: selectedTemplate
      });
      setEditedContent(res.data.body_md);
      setShowTemplates(false);
      toast.success("SOP generated successfully!");
      refreshUsage?.();
      fetchSops();
    } catch (e) {
      console.error("Generate error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate SOP");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!currentSop) return;
    
    try {
      await api.put(`/sops/${currentSop.id}`, {
        title: currentSop.title,
        body_md: editedContent
      });
      toast.success("SOP saved!");
      fetchSops();
    } catch (e) {
      console.error("Save error:", e);
      toast.error("Failed to save SOP");
    }
  };

  const handleDelete = async (sopId) => {
    if (!window.confirm("Are you sure you want to delete this SOP?")) return;
    
    try {
      await api.delete(`/sops/${sopId}`);
      toast.success("SOP deleted");
      if (currentSop?.id === sopId) {
        setCurrentSop(null);
        setEditedContent("");
        setShowTemplates(true);
      }
      fetchSops();
    } catch (e) {
      console.error("Delete error:", e);
      toast.error("Failed to delete SOP");
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedContent);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = () => {
    const blob = new Blob([editedContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${currentSop?.title || "SOP"}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("SOP exported!");
  };

  const loadSop = async (sopId) => {
    try {
      const res = await api.get(`/sops/${sopId}`);
      setCurrentSop(res.data);
      setEditedContent(res.data.body_md);
      setShowTemplates(false);
    } catch (e) {
      console.error("Load error:", e);
      toast.error("Failed to load SOP");
    }
  };

  return (
    <div className="space-y-6" data-testid="sop-generator">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500">SOP Generator</h1>
          <p className="text-indigo-400">Create standardized processes for your freelance business</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-indigo-500/20 text-indigo-400">
            {sops.length} SOP{sops.length !== 1 ? "s" : ""} saved
          </Badge>
          {!showTemplates && (
            <button
              onClick={() => {
                setShowTemplates(true);
                setCurrentSop(null);
                setEditedContent("");
              }}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              New SOP
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Templates / Saved SOPs */}
        <div className="lg:col-span-1 space-y-4">
          {/* Template Selection */}
          {showTemplates && (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-indigo-500 text-sm">Select Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(SOP_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTemplate(key)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedTemplate === key
                        ? "bg-indigo-500/20 border border-indigo-500/50"
                        : "bg-slate-900/30 hover:bg-slate-900/50"
                    }`}
                    data-testid={`sop-template-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{template.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-slate-600">{template.title}</p>
                        <p className="text-xs text-slate-400">{template.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Saved SOPs */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-indigo-500 text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Saved SOPs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
                </div>
              ) : sops.length > 0 ? (
                <div className="space-y-2">
                  {sops.map((sop) => (
                    <div
                      key={sop.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        currentSop?.id === sop.id
                          ? "bg-indigo-500/20 border border-indigo-500/50"
                          : "bg-slate-900/30 hover:bg-slate-900/50"
                      }`}
                    >
                      <button
                        onClick={() => loadSop(sop.id)}
                        className="flex-1 text-left"
                      >
                        <p className="text-sm text-slate-600 truncate">{sop.title}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(sop.updated_at).toLocaleDateString()}
                        </p>
                      </button>
                      <button
                        onClick={() => handleDelete(sop.id)}
                        className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">
                  No SOPs saved yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Generator / Editor */}
        <div className="lg:col-span-2">
          {showTemplates ? (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-indigo-500 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-400" />
                  Generate SOP
                </CardTitle>
                <CardDescription className="text-indigo-400">
                  Configure and generate your standardized process document
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Service Type */}
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Service Type</label>
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="web_design" className="text-slate-400">Web Design</SelectItem>
                      <SelectItem value="development" className="text-slate-400">Development</SelectItem>
                      <SelectItem value="graphic_design" className="text-slate-400">Graphic Design</SelectItem>
                      <SelectItem value="copywriting" className="text-slate-400">Copywriting</SelectItem>
                      <SelectItem value="consulting" className="text-slate-400">Consulting</SelectItem>
                      <SelectItem value="marketing" className="text-slate-400">Marketing</SelectItem>
                      <SelectItem value="other" className="text-slate-400">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Title (for custom template) */}
                {selectedTemplate === "custom" && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">SOP Title *</label>
                    <input
                      type="text"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="e.g., Weekly Client Check-in Process"
                      className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                      data-testid="custom-sop-title"
                    />
                  </div>
                )}

                {/* Additional Details */}
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Additional Details (optional)</label>
                  <Textarea
                    value={customDetails}
                    onChange={(e) => setCustomDetails(e.target.value)}
                    placeholder="Add any specific requirements, tools you use, or context for the SOP..."
                    className="min-h-[100px] bg-slate-900/50 border-slate-600 text-slate-600"
                    data-testid="sop-details-input"
                  />
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={generating || (selectedTemplate === "custom" && !customTitle.trim())}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-colors text-white disabled:bg-slate-600 ${
                    isOutOfCredits
                      ? "bg-[#B23A48] hover:bg-[#9c2e3b]"
                      : "bg-indigo-500 hover:bg-indigo-600"
                  }`}
                  data-testid="generate-sop-btn"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      {isOutOfCredits ? "Upgrade to continue" : "Generate SOP"}
                    </>
                  )}
                </button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-indigo-500">{currentSop?.title}</CardTitle>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopy}
                      className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                    </button>
                    <button
                      onClick={handleExport}
                      className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Export as Markdown"
                    >
                      <Download className="w-4 h-4 text-indigo-400" />
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg transition-colors text-sm"
                      data-testid="save-sop-btn"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[500px] font-mono text-sm bg-slate-900/50 border-slate-600 text-slate-600"
                  data-testid="sop-editor"
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
